import {Input, Search, Typography} from '@kaman/uikit';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import {useDispatch} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {setSheetHeader} from '../../../redux/sheetHeader/sheetHeaderSlice';
import OnlineSearchSkeleton from './onlineSearchSkeleton';
import {
    getOnlineSearchSections,
    STORE_QUERY_KEYS,
} from '../../../services/store.service';
import {SectionRenderer} from '../components/sectionRenderer';

const DEBOUNCE_MS = 300;

export default function OnlineSearch() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const categoryIdParam = searchParams.get('categoryId');
    const categoryId = categoryIdParam ? Number(categoryIdParam) : null;
    const hasCategory = categoryId !== null && Number.isFinite(categoryId);
    const tag = searchParams.get('tag');
    const hasTag = Boolean(tag);
    const hasFilter = hasCategory || hasTag;
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedQuery(query);
        }, DEBOUNCE_MS);

        return () => window.clearTimeout(timer);
    }, [query]);

    const {
        data: sections,
        isLoading,
        isSuccess,
    } = useQuery(
        [
            ...STORE_QUERY_KEYS.onlineSections,
            'search',
            debouncedQuery,
            categoryId,
            tag,
        ],
        () =>
            getOnlineSearchSections({
                query: debouncedQuery,
                categoryId: hasCategory ? categoryId : null,
                tag: tag || undefined,
            })
    );

    useEffect(() => {
        dispatch(
            setSheetHeader({
                title: 'جستجو',
                showBack: true,
                contentOverlap: true,
                contentPadding: '20px 16px',
            })
        );
    }, [dispatch]);

    const navigateToRoute = (route?: string | null) => {
        if (!route) return;
        if (/^https?:\/\//i.test(route)) {
            window.open(route, '_blank', 'noopener,noreferrer');
            return;
        }
        navigate(route);
    };

    const hasAnySectionItems =
        sections?.some((section) => (section.sectionItems?.length ?? 0) > 0) ??
        false;

    const showEmptyState =
        isSuccess && debouncedQuery.trim().length > 0 && !hasAnySectionItems;

    return (
        <main className="flex w-full min-w-0 flex-col gap-5" dir="rtl">
            {!hasFilter && (
                <div className="relative">
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder=""
                        aria-label="جستجو در فروشگاه های آنلاین"
                        variant="filled"
                        className="!border-transparent focus:!border-black !text-inherit"
                        rightIcon={
                            <Search size={24} className="text-[#848484]" />
                        }
                    />
                    {!query && (
                        <span
                            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-12 text-sm"
                            aria-hidden="true"
                        >
                            <span className="text-[#848484] text-[0.8rem] font-semibold">
                                جستجو در&nbsp;
                            </span>
                            <span className="text-[#0158CC] text-[0.8rem] font-semibold">
                                فروشگاه های آنلاین
                            </span>
                        </span>
                    )}
                </div>
            )}

            {isLoading ? (
                <OnlineSearchSkeleton showSearchBar={false} />
            ) : showEmptyState ? (
                <div className="flex w-full flex-col items-center justify-center gap-3 pt-10 text-center">
                    <img
                        src="/images/no-results-illustration.svg"
                        alt=""
                        className="h-auto w-[220px] max-w-full"
                    />
                    <Typography as="h2" variant="h6" className="text-gray-900">
                        کالایی با این مشخصات پیدا نکردیم
                    </Typography>
                    <Typography variant="body-small" color="gray">
                        پیشنهاد می‌کنیم فیلترها را تغییر دهید
                    </Typography>
                </div>
            ) : (
                sections?.map((section, index) => (
                    <div
                        key={`online-search-${index}`}
                        className="w-full min-w-0 overflow-x-hidden"
                    >
                        <SectionRenderer
                            section={section}
                            onNavigate={navigateToRoute}
                            storeChannel="online"
                            productsLayout={
                                debouncedQuery.trim() || hasFilter
                                    ? 'grid'
                                    : 'carousel'
                            }
                        />
                    </div>
                ))
            )}
        </main>
    );
}
